import apiConfig from "../config/apiConfig";
import { getCookie } from "./enum";

const getHeaders = () => {
  const accessToken = getCookie("access_token");
  return {
    "Content-Type": "application/json",
    Authorization: accessToken ? `Bearer ${accessToken}` : "",
  };
};

export const createAttendanceSession = async (data: {
  program_id: string;
  campus_location_id: string;
  session_label: string;
  scheduled_date?: string;
  scheduled_start_time?: string;
}) => {
  const res = await fetch(`${apiConfig.API_BASE_URL}/attendance/sessions`, {
    method: "POST",
    headers: getHeaders(),
    credentials: "include",
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(errText || "Failed to create attendance session");
  }
  return res.json();
};

export const startAttendanceSession = async (sessionId: string) => {
  const res = await fetch(`${apiConfig.API_BASE_URL}/attendance/sessions/${sessionId}/start`, {
    method: "POST",
    headers: getHeaders(),
    credentials: "include",
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(errText || "Failed to start attendance session");
  }
  return res.json();
};

export const closeAttendanceSession = async (sessionId: string) => {
  const res = await fetch(`${apiConfig.API_BASE_URL}/attendance/sessions/${sessionId}/close`, {
    method: "POST",
    headers: getHeaders(),
    credentials: "include",
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(errText || "Failed to close attendance session");
  }
  return res.json();
};

export const getDisplayData = async (sessionId: string) => {
  const res = await fetch(`${apiConfig.API_BASE_URL}/attendance/sessions/${sessionId}/display`, {
    method: "GET",
    headers: getHeaders(),
    credentials: "include",
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(errText || "Failed to fetch QR display data");
  }
  return res.json();
};

export const getSessionRecords = async (sessionId: string, page = 0, size = 50) => {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  const res = await fetch(`${apiConfig.API_BASE_URL}/attendance/sessions/${sessionId}/records?${params.toString()}`, {
    method: "GET",
    headers: getHeaders(),
    credentials: "include",
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(errText || "Failed to fetch session records");
  }
  return res.json();
};

export const markManualAttendance = async (
  sessionId: string,
  data: {
    student_id: string;
    coordinator_lat?: number;
    coordinator_lon?: number;
    location_accuracy?: number;
    reason: string;
  }
) => {
  const res = await fetch(`${apiConfig.API_BASE_URL}/attendance/sessions/${sessionId}/manual`, {
    method: "POST",
    headers: getHeaders(),
    credentials: "include",
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(errText || "Failed to mark manual attendance");
  }
  return res.json();
};

export const submitQrAttendance = async (data: {
  session_id: string;
  qr_token: string;
  student_lat?: number;
  student_lon?: number;
  location_accuracy?: number;
}) => {
  const res = await fetch(`${apiConfig.API_BASE_URL}/attendance/mark`, {
    method: "POST",
    headers: getHeaders(),
    credentials: "include",
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const errText = await res.text();
    try {
      const jsonErr = JSON.parse(errText);
      throw new Error(jsonErr.message || "Failed to mark attendance");
    } catch {
      throw new Error(errText || "Failed to mark attendance");
    }
  }
  return res.json();
};

export const getStudentActiveSessions = async () => {
  const res = await fetch(`${apiConfig.API_BASE_URL}/attendance/my/active`, {
    method: "GET",
    headers: getHeaders(),
    credentials: "include",
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(errText || "Failed to fetch active student sessions");
  }
  return res.json();
};

export const getStudentHistory = async (page = 0, size = 20) => {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  const res = await fetch(`${apiConfig.API_BASE_URL}/attendance/my/history?${params.toString()}`, {
    method: "GET",
    headers: getHeaders(),
    credentials: "include",
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(errText || "Failed to fetch student attendance history");
  }
  return res.json();
};

export const getStudentSummary = async () => {
  const res = await fetch(`${apiConfig.API_BASE_URL}/attendance/my/summary`, {
    method: "GET",
    headers: getHeaders(),
    credentials: "include",
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(errText || "Failed to fetch student summary");
  }
  return res.json();
};

export const getCourseSummary = async (programId: string) => {
  const res = await fetch(`${apiConfig.API_BASE_URL}/attendance/courses/${programId}/summary`, {
    method: "GET",
    headers: getHeaders(),
    credentials: "include",
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(errText || "Failed to fetch course summary");
  }
  return res.json();
};

export const getAllSessionsHistory = async (page = 0, size = 100) => {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  const res = await fetch(`${apiConfig.API_BASE_URL}/attendance/sessions/history?${params.toString()}`, {
    method: "GET",
    headers: getHeaders(),
    credentials: "include",
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(errText || "Failed to fetch sessions history");
  }
  return res.json();
};
